import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Textarea } from '@monksflow/monks-ui';
import { FormDialog } from '@/shared/ui/FormDialog';

export interface TaxonomyReasonModalProps {
  open: boolean;
  title: string;
  /** Short line under the title explaining the consequence. */
  description?: string;
  /**
   * Extra content between the description and the reason field — used to show
   * the blast radius of a publish before it's confirmed.
   */
  extra?: ReactNode;
  /** Label for the confirm button. */
  confirmLabel: string;
  /**
   * Refuses the action for a reason the caller can already see, independently of
   * whether a justification has been typed. `extra` is expected to say why.
   */
  confirmDisabled?: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  /** Called with the (required, trimmed) justification when confirmed. */
  onConfirm: (reason: string) => void;
}

/**
 * Captures the mandatory `reason` justification every taxonomy lifecycle change
 * requires (publish / archive / delete). Chrome + footer come from the shared
 * `FormDialog` (same pattern as the Brief Templates modals) so spacing and the
 * primary/secondary actions are consistent. The confirm button stays disabled
 * until a non-empty reason is entered; reason resets whenever the modal
 * (re)opens so a prior entry never leaks into the next action.
 */
export function TaxonomyReasonModal(props: TaxonomyReasonModalProps) {
  const { t } = useTranslation('knowledge');
  const reasonFieldId = useId();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (props.open) {
      setReason('');
    }
  }, [props.open]);

  const trimmed = reason.trim();
  const canConfirm = trimmed.length > 0 && !props.isSubmitting && !props.confirmDisabled;

  const handleOpenChange = (next: boolean) => {
    if (props.isSubmitting) return;
    if (!next) props.onCancel();
  };

  return (
    <FormDialog
      open={props.open}
      onOpenChange={handleOpenChange}
      title={props.title}
      className="flex max-h-[calc(100dvh-64px)] flex-col sm:max-w-120"
    >
      <FormDialog.Body className="flex flex-col gap-4">
        {props.description && <p className="text-sm text-muted-foreground">{props.description}</p>}
        {props.extra}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={reasonFieldId}>
            {t('taxonomy.taxonomyReasonModal.reason')} <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id={reasonFieldId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('taxonomy.taxonomyReasonModal.explainWhyRecordedAuditTrail')}
            rows={3}
            maxLength={1000}
            className="bg-input"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
            autoFocus
          />
        </div>
      </FormDialog.Body>

      <FormDialog.Footer
        primary={{
          label: props.confirmLabel,
          onClick: () => canConfirm && props.onConfirm(trimmed),
          disabled: !canConfirm,
          loading: props.isSubmitting,
        }}
        secondary={{ label: t('taxonomy.taxonomyReasonModal.cancel'), onClick: props.onCancel }}
      />
    </FormDialog>
  );
}

TaxonomyReasonModal.displayName = 'TaxonomyReasonModal';
